<template>
  <div class="stall-products-page">
    <!-- 主体布局 -->
    <div class="main-content">
      <!-- 头部：搜索 + 购物车 -->
      <div class="product-header">
        <div class="search-wrapper">
          <div class="search-box" @click="goToSearch">
            <span class="search-icon">🔍</span>
            <span class="search-placeholder">{{ searchKeyword || $t('stallProduct.searchProducts') }}</span>
          </div>
        </div>
        <button class="cart-icon" @click="openCart">
          🛒 <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
        </button>
        <button class="scan-btn" @click="scanQRCode" :title="$t('stallProduct.scanOrder')">
          <svg class="scan-frame-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
            <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
            <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
            <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
            <line x1="7" y1="12" x2="17" y2="12"/>
          </svg>
          <span class="scan-badge">{{ $t('customer.StallProducts.line24_text') }}</span>
        </button>
      </div>

      <!-- 分类标签栏 -->
      <div class="category-labels">
        <div 
          :class="['category-label', { active: selectedCategory === '' }]"
          @click="selectedCategory = ''"
        >
          {{ $t('stallProduct.all') }}
        </div>
        <div 
          v-for="cat in categories" 
          :key="cat.value"
          :class="['category-label', { active: selectedCategory === cat.value }]"
          @click="selectedCategory = cat.value"
        >
          <span class="category-emoji">{{ cat.emoji }}</span>
          <span class="category-text">{{ cat.name }}</span>
        </div>
      </div>

      <!-- 商品列表 -->
      <div class="product-list">
          <div v-for="product in filteredProducts" :key="product.id" class="product-item">
            <!-- 商品名称标题 - 整行 -->
            <div class="product-name-title">{{ product.name }}</div>
            
            <!-- 下面左图右信息 -->
            <div class="product-body">
            <!-- 图片 -->
            <div class="product-image" @click.stop="openImagePreview(product)">
              <img 
                v-if="product.mainImage" 
                :src="getImageUrl(product.mainImage)" 
                :alt="$t('stallProduct.productImage')"
                class="product-img"
                @error="product.imageError = true"
              />
              <div v-else class="product-icon">📦</div>
              <!-- 多图指示 -->
              <div v-if="getProductImages(product).length > 1" class="image-count-badge">
                📷 {{ getProductImages(product).length }}
              </div>
            </div>
            
            <!-- 右侧信息 -->
            <div class="product-info">
              <div class="product-meta">
                <span v-if="product.wholesalerName" class="wholesaler-name">🏪 {{ product.wholesalerName }}</span>
                <span v-if="product.wholesalerName && product.categoryName"> | </span>
                <span>{{ product.categoryName || '' }}</span>
                <span v-if="product.sku || product.id"> · SKU: {{ product.sku || product.id }}</span>
              </div>
              
              <div class="price-column">
                <div class="price-item" v-if="product.wholesalePrice || product.minWholesalePrice">
                  <span class="price-value market-price-del">S$ {{ formatPrice(product.marketPrice || 0) }}</span>
                  <span class="price-value wholesale-price">S$ {{ formatPrice((product.wholesalePrice || product.minWholesalePrice) || 0) }}</span>
                  <span class="wholesale-badge">{{ $t('stallProduct.wholesale') }}</span>
                </div>
                <div class="price-item" v-else>
                  <span class="price-value">S$ {{ formatPrice(product.marketPrice || 0) }}</span>
                </div>
              </div>
              
              <div class="stock-row" v-if="product.availableStock !== undefined">
                <span class="stock-label">{{ $t('stallProduct.availableStock') }}：</span>
                <span :class="['stock-value', product.availableStock > 0 ? 'in-stock' : 'out-of-stock']">
                  {{ product.availableStock > 0 ? product.availableStock.toFixed(2) : $t('stallProduct.soldOut') }} {{ product.unit || $t('common.unknown') }}
                </span>
              </div>
              <div class="stock-row">
                <span class="stock-label">{{ $t('stallProduct.book') }}：</span>
                <div class="quantity-display" @click.stop="openQuantityPicker(product)">
                  {{ cartQuantities[product.id] || 1 }}
                  <span class="stock-unit">{{ product.unit || $t('stallProduct.jin') }}</span>
                  <span class="edit-arrow">▾</span>
                </div>
              </div>
              <!-- 第三行：加入购物车按钮 + 联系商家 + 分享按钮 -->
              <div class="stock-row action-row">
                <button 
                  class="add-to-cart-btn" 
                  @click.stop="addToCart(product)"
                  :disabled="product.availableStock !== undefined && product.availableStock <= 0">
                  {{ product.availableStock !== undefined && product.availableStock <= 0 ? $t('stallProduct.soldOut') : $t('stallProduct.addToCart') }}
                </button>
                <button 
                  class="whatsapp-btn" 
                  @click.stop="contactWhatsapp(product)"
                  :disabled="!wholesalerWhatsapp"
                  :title="$t('order.contactMerchant')">
                  💬 {{ $t('order.contactMerchant') }}
                </button>
                <button 
                  class="share-btn" 
                  @click.stop="showShareDialog(product)"
                  :title="campaignPrizeName ? `赢${campaignPrizeName}` : $t('common.share')">
                  <svg class="share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="18" cy="5" r="3"></circle>
                    <circle cx="6" cy="12" r="3"></circle>
                    <circle cx="18" cy="19" r="3"></circle>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                  </svg>
                  <span v-if="campaignPrizeName" class="share-btn-text">{{ $t('common.win') }}{{ campaignPrizeName }}</span>
                </button>
              </div>
            </div>
            </div>
          </div>

          <div v-if="filteredProducts.length === 0 && !loading" class="empty-state">
            <div class="empty-icon">📦</div>
            <p>{{ $t('stallProduct.noProducts') }}</p>
          </div>
          
          <!-- 加载状态 -->
          <div v-if="loadingMore" class="loading-more">
            <span class="loading-spinner">⏳</span> {{ $t('common.loading') }}
          </div>
          <div v-if="!hasMore && products.length > 0" class="no-more">
            {{ $t('common.noMore') }}
          </div>
        </div>
      </div>

    <!-- 购物车弹窗 -->
    <div v-if="showCart" class="modal-overlay" @click.self="showCart = false">
      <div class="modal cart-modal">
        <div class="modal-header">
          <h2>🛒 {{ $t('stallProduct.cart') }}</h2>
          <button class="close-btn" @click="closeCart">×</button>
        </div>
        <div class="modal-body">
          <div v-if="cartItems.length === 0" class="empty-cart">
            <div class="empty-icon">🛒</div>
            <p>{{ $t('stallProduct.cartEmpty') }}</p>
          </div>
          <div v-else>
            <!-- 批量操作栏 -->
            <div class="batch-actions">
              <label class="select-all-label">
                <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" />
                <span>{{ $t('stallProduct.selectAll') }}</span>
              </label>
              <div class="batch-buttons">
                <button class="btn-delete-batch" @click="deleteSelected" :disabled="selectedIds.length === 0">
                  {{ $t('stallProduct.deleteSelected') }} ({{ selectedIds.length }})
                </button>
                <button class="btn-clear-cart" @click="clearCart">
                  {{ $t('stallProduct.clearCart') }}
                </button>
              </div>
            </div>
            
            <!-- 商品列表 -->
            <div v-for="item in cartItems" :key="item.id" class="cart-item">
              <div class="item-checkbox">
                <input type="checkbox" v-model="selectedIds" :value="item.id" />
              </div>
              <div class="item-info">
                <div class="item-name">{{ item.name }}</div>
                <div class="item-wholesaler" v-if="item.wholesalerName">🏪 {{ item.wholesalerName }}</div>
                <div class="item-price">S$ {{ formatPrice(item.price || 0) }} x {{ item.quantity }}</div>
                <div class="item-tax" v-if="item.taxRate && item.taxRate > 0">
                  📋 {{ $t('stallProduct.taxRate') }} {{ (item.taxRate * 100).toFixed(1) }}%
                </div>
                <div class="item-tax item-tax-free" v-else-if="item.taxRate === 0">
                  📋 {{ $t('stallProduct.taxFree') }}
                </div>
              </div>
              <div class="item-actions">
                <button @click="decreaseQty(item)">-</button>
                <span>{{ item.quantity }}</span>
                <button @click="increaseQty(item)">+</button>
                <button class="btn-delete-item" @click="deleteItem(item)" :title="$t('common.delete')">
                  🗑️
                </button>
              </div>
            </div>
            <div class="cart-summary">
              <div class="summary-row">
                <span>{{ $t('stallProduct.subtotal') }}:</span>
                <span>S$ {{ selectedSubtotal.toFixed(2) }}</span>
              </div>
              <div class="summary-row">
                <span>{{ $t('stallProduct.gstTax') }}:</span>
                <span>S$ {{ selectedGst.toFixed(2) }}</span>
              </div>
              <div class="summary-row grand-total">
                <span>{{ $t('stallProduct.total') }}:</span>
                <span>S$ {{ selectedTotalWithGst.toFixed(2) }}</span>
              </div>
              <div v-if="selectedIds.length < cartItems.length" class="summary-note">
                {{ $t('stallProduct.onlySelected') }}
              </div>
            </div>
          </div>
          <div v-if="cartItems.length > 0" class="modal-footer">
            <button class="modal-btn secondary" @click="clearCart" v-if="cartItems.length > 0">
              {{ $t('stallProduct.clearCart') }}
            </button>
            <button class="modal-btn primary" @click="checkout" :disabled="selectedIds.length === 0">
              {{ $t('stallProduct.checkout') }} {{ selectedIds.length > 0 ? `(${selectedIds.length})` : '' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 数量选择弹窗 -->
    <div v-if="showQuantityPicker" class="modal-overlay" @click.self="closeQuantityPicker">
      <div class="modal quantity-picker-modal">
        <div class="modal-header">
          <h2>{{ $t('stallProduct.book') }} {{ currentPickerProduct && currentPickerProduct.name ? currentPickerProduct.name : '' }} {{ currentPickerProduct && currentPickerProduct.unit ? currentPickerProduct.unit : $t('stallProduct.jin') }}</h2>
          <button class="close-btn" @click="closeQuantityPicker">×</button>
        </div>
        <div class="modal-body">
          <!-- 滚轮选择 -->
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div 
              class="picker-scroll" 
              ref="modalPickerScroll"
              @scroll="onModalPickerScroll"
            >
              <div class="picker-spacer"></div>
              <div class="picker-item" v-for="qty in getQuantityOptions(currentPickerProduct)" :key="qty">
                {{ qty }}
              </div>
              <div class="picker-spacer"></div>
            </div>
          </div>
          <!-- 手动输入 -->
          <div class="quantity-input-section">
            <label>{{ $t('stallProduct.customQuantity') }}：</label>
            <input 
              type="number" 
              v-model="customQuantity"
              :min="currentPickerProduct && currentPickerProduct.minOrderQuantity ? currentPickerProduct.minOrderQuantity : 0.5"
              :max="currentPickerProduct && currentPickerProduct.availableStock ? currentPickerProduct.availableStock : 9999"
              :step="1"
              class="quantity-input"
              @input="onCustomQuantityInput"
            />
            <span class="stock-unit">{{ currentPickerProduct && currentPickerProduct.unit ? currentPickerProduct.unit : $t('stallProduct.jin') }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn primary" @click="confirmQuantity">
            {{ $t('stallProduct.addToCart') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item active">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('stallProduct.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('/stall/cart')">
        <span class="nav-item-icon">🛒</span>
        <span>{{ $t('navigation.cart') }}</span>
        <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('/stall/orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('navigation.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('/stall/profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('navigation.profile') }}</span>
      </button>
    </nav>

    <!-- 图片预览 -->
    <ImagePreview ref="imagePreview" />

    <!-- 分享弹窗 -->
    <StallShareDialog
      :visible.sync="shareDialogVisible"
      :product="shareProduct"
    />
  </div>
</template>

<script>
import api from '@/services/api';
import ImagePreview from '@/components/ImagePreview.vue';
import StallShareDialog from '@/components/StallShareDialog.vue';
import { getImageUrl } from '@/utils/image';

export default {
  components: { ImagePreview, StallShareDialog },
  data() {
    return {
      searchKeyword: '',
      selectedCategory: '',
      showCart: false,
      cartQuantities: {},  // 存储每个商品的预订数量
      products: [],
      cart: this.loadCartFromLocalStorage(),  // 从 localStorage 加载购物车
      selectedIds: [],  // 选中的商品 ID
      selectAll: false,  // 全选状态
      
      // 搜索下拉提示
      showSuggestions: false,
      suggestions: [],
      suggestionTimer: null,
      
      // 确认弹窗（使用 Element UI MessageBox）
      // confirmVisible: false,
      // confirmMessage: '',
      // confirmCallback: null,
      // confirmIcon: '⚠️',
      
      categories: [],  // 动态加载
      
      // 分享弹窗
      shareDialogVisible: false,
      shareProduct: null,
      campaignPrizeName: '',
      
      // WhatsApp 联系商家
      wholesalerWhatsapp: '',
      
      // 数量选择弹窗
      showQuantityPicker: false,
      currentPickerProduct: null,
      customQuantity: 1,
      
      // 分页相关
      page: 0,
      pageSize: 20,
      totalPages: 0,
      hasMore: true,
      loading: false,
      loadingMore: false
    };
  },
  computed: {
    filteredProducts() {
      if (!this.selectedCategory || this.selectedCategory === '') {
        return this.products;
      }
      const cat = this.categories.find(c => c.value === this.selectedCategory);
      if (!cat) return this.products;
      const keywords = cat.name.replace(/类/g, '');
      return this.products.filter(p => {
        const cn = (p.categoryName || '').toLowerCase();
        return cn.includes(keywords.toLowerCase());
      });
    },
    cartItems() {
      return this.cart.filter(item => item.quantity > 0);
    },
    cartCount() {
      return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    },
    // 全部商品的金额（用于显示）
    subtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },
    gst() {
      return this.cartItems.reduce((sum, item) => {
        const taxRate = item.taxRate || 0
        return sum + (item.price * item.quantity * taxRate)
      }, 0)
    },
    totalWithGst() {
      return this.subtotal + this.gst;
    },
    // 选中商品的金额（用于结算）
    selectedItems() {
      return this.cartItems.filter(item => this.selectedIds.includes(item.id));
    },
    selectedSubtotal() {
      return this.selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },
    selectedGst() {
      return this.selectedItems.reduce((sum, item) => {
        const taxRate = item.taxRate || 0
        return sum + (item.price * item.quantity * taxRate)
      }, 0)
    },
    selectedTotalWithGst() {
      return this.selectedSubtotal + this.selectedGst;
    }
  },
  created() {
    // 从路由参数获取搜索关键词（从搜索页跳转过来）
    if (this.$route.query.keyword) {
      this.searchKeyword = this.$route.query.keyword
    }
    // 从路由参数获取分类（兼容旧逻辑）
    if (this.$route.query.category) {
      this.selectedCategory = this.$route.query.category
    }
    this.loadCategories();
    this.fetchProducts();
    this.loadCartFromLocalStorage();
    this.loadActiveCampaign();
    this.loadWholesalerWhatsapp();
  },
  mounted() {
    // 设置滚轮初始位置
    this.$nextTick(() => {
      this.products.forEach(product => {
        this.setPickerInitialPosition(product);
      });
    });
    // 监听滚动事件
    window.addEventListener('scroll', this.handleScroll);
  },
  watch: {
    // 监听路由参数变化（从搜索页跳转过来）
    '$route.query.keyword': {
      handler(newKeyword) {
        if (newKeyword && newKeyword !== this.searchKeyword) {
          this.searchKeyword = newKeyword;
          this.fetchProducts();
        }
      }
    },
    '$route.query.category': {
      handler(newCategory) {
        if (newCategory && newCategory !== this.selectedCategory) {
          this.selectedCategory = newCategory;
          this.fetchProducts();
        }
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    getImageUrl,
    /**
     * 获取商品图片列表
     */
    getProductImages(product) {
      const images = [];
      if (product.mainImage) {
        images.push(this.getImageUrl(product.mainImage));
      }
      if (product.images) {
        let imgList = product.images;
        if (typeof imgList === 'string') {
          try { imgList = JSON.parse(imgList); } catch(e) { imgList = []; }
        }
        if (Array.isArray(imgList)) {
          imgList.forEach(img => {
            const url = typeof img === 'string' ? img : img.url;
            if (url && !images.includes(this.getImageUrl(url))) {
              images.push(this.getImageUrl(url));
            }
          });
        }
      }
      return images;
    },
    
    /**
     * 滚动加载更多
     */
    handleScroll() {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      if (scrollHeight - scrollTop - clientHeight < 100 && !this.loadingMore && this.hasMore) {
        this.loadMore();
      }
    },
    /**
     * 加载更多商品
     */
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      
      this.loadingMore = true;
      try {
        const customerId = localStorage.getItem('stored_customer_id');
        // ⭐ 从 sessionStorage 获取批发商ID
        const wholesalerId = sessionStorage.getItem('url_wholesaler_id');
        const params = {
          customerId,
          page: this.page + 1,
          size: this.pageSize
        };
        if (wholesalerId) {
          params.wholesalerId = wholesalerId;
        }
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim();
        }
        
        const response = await api.get('/customer/products', { params });
        
        let newProducts = [];
        if (response?.items) {
          newProducts = response.items;
        } else if (response?.products) {
          newProducts = response.products;
        } else if (response?.content) {
          newProducts = response.content;
        } else if (Array.isArray(response)) {
          newProducts = response;
        }
        
        if (newProducts.length > 0) {
          this.products = [...this.products, ...newProducts];
          // 初始化新商品的预订数量
          newProducts.forEach(p => {
            if (!this.cartQuantities[p.id]) {
              this.cartQuantities[p.id] = 1;
            }
          });
        }
        
        // 更新分页状态
        if (response?.totalPages !== undefined) {
          this.page = response.page != null ? response.page : this.page + 1;
          this.totalPages = response.totalPages;
          this.hasMore = response.hasMore != null ? response.hasMore : (this.page < this.totalPages - 1);
        } else {
          // 如果没有分页信息，根据返回数量判断
          this.hasMore = newProducts.length >= this.pageSize;
          this.page++;
        }
      } catch (error) {
        console.error('加载更多商品失败:', error);
      } finally {
        this.loadingMore = false;
      }
    },
    /**
     * 设置滚轮初始位置
     */
    setPickerInitialPosition(product) {
      const ref = this.$refs['picker-' + product.id];
      if (!ref) return;
      const el = ref[0] || ref;
      const qty = this.cartQuantities[product.id] || 1;
      const options = this.getQuantityOptions(product);
      const index = options.indexOf(qty);
      if (index >= 0) {
        el.scrollTop = index * 32;
      }
    },
    /**
     * 生成数量选项
     */
    getQuantityOptions(product) {
      const options = [];
      const max = Math.min(product.availableStock || 9999, 50);
      for (let i = 1; i <= max; i++) {
        options.push(i);
      }
      return options;
    },
    /**
     * 滚轮滚动事件（防抖）
     */
    onPickerScroll(product) {
      if (this._scrollTimer) {
        clearTimeout(this._scrollTimer);
      }
      this._scrollTimer = setTimeout(() => {
        const ref = this.$refs['picker-' + product.id];
        if (!ref) return;
        const el = ref[0] || ref;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        const options = this.getQuantityOptions(product);
        if (options[index] !== undefined) {
          this.cartQuantities[product.id] = options[index];
        }
      }, 100);
    },
    openCart() {
      this.showCart = true;
    },
    /**
     * 打开数量选择弹窗
     */
    openQuantityPicker(product) {
      this.currentPickerProduct = product;
      this.customQuantity = this.cartQuantities[product.id] || 1;
      this.showQuantityPicker = true;
      this.$nextTick(() => {
        const el = this.$refs.modalPickerScroll;
        if (el) {
          const qty = this.cartQuantities[product.id] || 1;
          const options = this.getQuantityOptions(product);
          const index = options.indexOf(qty);
          if (index >= 0) {
            el.scrollTop = spacerHeight + index * itemHeight;
          }
        }
      });
    },
    /**
     * 关闭数量选择弹窗
     */
    closeQuantityPicker() {
      this.showQuantityPicker = false;
      this.currentPickerProduct = null;
    },
    /**
     * 弹窗滚轮滚动事件
     */
    onModalPickerScroll() {
      if (this._modalScrollTimer) {
        clearTimeout(this._modalScrollTimer);
      }
      this._modalScrollTimer = setTimeout(() => {
        const el = this.$refs.modalPickerScroll;
        if (!el || !this.currentPickerProduct) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.max(0, Math.round(scrollTop / itemHeight));
        const options = this.getQuantityOptions(this.currentPickerProduct);
        if (options[index] !== undefined) {
          this.customQuantity = options[index];
        }
      }, 100);
    },
    /**
     * 手动输入数量
     */
    onCustomQuantityInput() {
      const qty = parseFloat(this.customQuantity);
      if (isNaN(qty) || qty <= 0) return;
      const max = this.currentPickerProduct?.availableStock || 9999;
      if (qty > max) {
        this.customQuantity = max;
      }
      // 同步滚轮位置
      const el = this.$refs.modalPickerScroll;
      if (el) {
        const options = this.getQuantityOptions(this.currentPickerProduct);
        const index = options.indexOf(qty);
        if (index >= 0) {
          el.scrollTop = 64 + index * 32;
        }
      }
    },
    /**
     * 确认数量
     */
    confirmQuantity() {
      if (this.currentPickerProduct && this.customQuantity > 0) {
        this.cartQuantities[this.currentPickerProduct.id] = this.customQuantity;
        this.addToCart(this.currentPickerProduct);
      }
      this.closeQuantityPicker();
    },
    scanQRCode() {
      this.$router.push('/stall/scan-qr');
    },
    showShareDialog(product) {
      this.shareProduct = product;
      this.shareDialogVisible = true;
    },
    onShareCreated(shareData) {
      console.log('分享创建成功:', shareData);
      this.shareDialogVisible = false;
      this.$message.success('分享链接已生成');
    },
    closeCart() {
      this.showCart = false;
    },
    toggleCart() {
      this.showCart = !this.showCart;
    },

    /**
     * 加载有商品的分类
     */
    async loadCategories() {
      try {
        const customerId = localStorage.getItem('stored_customer_id');
        const response = await api.get('/customer/products/categories', { params: { customerId } });
        this.categories = (response || []).map(cat => ({
          value: cat.name,
          name: cat.name,
          emoji: cat.emoji || '📦'
        }));
      } catch (error) {
        console.error('加载分类失败:', error);
      }
    },
    async fetchProducts() {
      this.loading = true;
      this.page = 0;
      this.products = [];
      this.hasMore = true;
      try {
        const customerId = localStorage.getItem('stored_customer_id');
        // ⭐ 从 sessionStorage 获取批发商ID（从 /wholesale/:id 进来时保存的）
        const wholesalerId = sessionStorage.getItem('url_wholesaler_id');
        const params = {
          customerId,
          page: 0,
          size: this.pageSize
        };
        if (wholesalerId) {
          params.wholesalerId = wholesalerId;
        }
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim();
        }
        if (this.selectedCategory) {
          params.categoryKeyword = this.selectedCategory;
        }
        const response = await api.get('/customer/products', { params });
        console.log('API 响应:', response);
        
        // 处理不同的响应格式
        let rawProducts = [];
        if (response?.items) {
          rawProducts = response.items;
        } else if (response?.products) {
          rawProducts = response.products;
        } else if (response?.content) {
          rawProducts = response.content;
        } else if (Array.isArray(response)) {
          rawProducts = response;
        }
        
        // 规范化图片URL
        this.products = rawProducts.map(p => this.normalizeProduct(p));
        
        // 更新分页状态
        if (response?.totalPages !== undefined) {
          this.page = response.page != null ? response.page : 0;
          this.totalPages = response.totalPages;
          this.hasMore = response.hasMore != null ? response.hasMore : (this.page < this.totalPages - 1);
        } else {
          this.hasMore = this.products.length >= this.pageSize;
        }
        
        console.log('最终商品列表:', this.products);
        console.log('商品数量:', this.products.length);
        
        // 初始化每个商品的预订数量为 1
        this.products.forEach(p => {
          if (!this.cartQuantities[p.id]) {
            this.cartQuantities[p.id] = 1;
          }
        });
        
        // 商品加载完成后加载 WhatsApp 号码
        await this.loadWholesalerWhatsapp();
      } catch (error) {
        console.error('加载商品失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 规范化商品图片URL
    normalizeProduct(product) {
      return {
        ...product,
        mainImage: this.normalizeImageUrl(product.mainImage),
        images: this.normalizeImageUrls(product.images)
      };
    },
    
    // 规范化单个图片URL
    normalizeImageUrl(url) {
      if (!url) return url;
      if (url.startsWith('http')) {
        try {
          const urlObj = new URL(url);
          return urlObj.pathname; // 返回 /api/uploads/... 格式
        } catch (e) {
          return url;
        }
      }
      return url;
    },
    
    // 规范化图片URL数组
    normalizeImageUrls(images) {
      if (!images) return images;
      let imgList = images;
      if (typeof imgList === 'string') {
        try { imgList = JSON.parse(imgList); } catch(e) { return images; }
      }
      if (Array.isArray(imgList)) {
        return imgList.map(img => {
          const url = typeof img === 'string' ? img : img.url;
          return this.normalizeImageUrl(url);
        });
      }
      return images;
    },
    async search() {
      // 搜索时重置分页
      await this.fetchProducts();
    },
    
    /**
     * 跳转到搜索页面
     */
    goToSearch() {
      this.$router.push('/customer/search')
    },
    
    /**
     * 搜索框输入（防抖）
     */
    onSearchInput() {
      if (this.suggestionTimer) {
        clearTimeout(this.suggestionTimer)
      }
      this.suggestionTimer = setTimeout(() => {
        this.loadSuggestions()
      }, 200)
      // 同时触发商品搜索
      if (this._searchTimer) {
        clearTimeout(this._searchTimer)
      }
      this._searchTimer = setTimeout(() => {
        this.search()
      }, 300)
    },
    
    /**
     * 加载搜索建议
     */
    async loadSuggestions() {
      const keyword = this.searchKeyword.trim()
      if (!keyword) {
        this.suggestions = []
        return
      }
      
      const results = []
      
      // 从分类中匹配
      this.categories.forEach(cat => {
        if (cat.name.toLowerCase().includes(keyword.toLowerCase())) {
          results.push({ type: 'category', name: cat.name, value: cat.value })
        }
      })
      
      // 从已加载的商品中匹配
      this.products.forEach(product => {
        if (product.name && product.name.toLowerCase().includes(keyword.toLowerCase())) {
          results.push({ type: 'product', name: product.name, value: product.name })
        }
      })
      
      // 最多显示 8 条
      this.suggestions = results.slice(0, 8)
    },
    
    /**
     * 选择搜索建议
     */
    selectSuggestion(item) {
      if (item.type === 'category') {
        this.selectedCategory = item.value
        this.searchKeyword = ''
        this.suggestions = []
      } else {
        this.searchKeyword = item.value
        this.search()
        this.suggestions = []
      }
    },
    
    /**
     * 隐藏搜索建议
     */
    hideSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false
      }, 200)
    },
    
    /**
     * 清除搜索
     */
    clearSearch() {
      this.searchKeyword = ''
      this.search()
    },
    
    /**
     * 搜索商品
     */
    async search() {
      // 重置分页并重新加载
      this.page = 0
      this.products = []
      this.hasMore = true
      await this.fetchProducts()
    },
    
    /**
     * 统一的确认方法（使用 Element UI MessageBox）
     */
    async showConfirm(message, icon = '⚠️') {
      console.log('showConfirm called with message:', message);
      console.log('this.$confirm:', this.$confirm);
      
      try {
        await this.$confirm(message, this.$t('stallProduct.confirmTitle'), {
          confirmButtonText: this.$t('stallProduct.confirmBtn'),
          cancelButtonText: this.$t('stallProduct.cancelBtn'),
          type: 'warning',
          closeOnClickModal: false
        });
        console.log('User confirmed');
        return true;
      } catch (error) {
        console.log('User cancelled or error:', error);
        return false;
      }
    },
    
    async addToCart(product) {
      const quantity = this.cartQuantities[product.id] || 1;
      
      // ✅ 库存验证：检查是否超过可用库存
      if (product.availableStock !== undefined && product.availableStock > 0) {
        // 检查购物车中该商品的现有数量
        const existingInCart = this.cart.find(item => item.id === product.id);
        const currentQtyInCart = existingInCart ? existingInCart.quantity : 0;
        const totalQty = currentQtyInCart + quantity;
        
        if (totalQty > product.availableStock) {
          const message = this.$t('stallProduct.stockInsufficient') + `：${product.availableStock.toFixed(2)} ${product.unit || this.$t('common.unknown')}，\n` + this.$t('stallProduct.alreadyInCart') + `：${currentQtyInCart.toFixed(2)} ${product.unit || this.$t('common.unknown')}，\n` + this.$t('stallProduct.wantToAdd') + `：${quantity} ${product.unit || this.$t('common.unknown')}`;
          
          this.$toast?.error(message);
          return;
        }
      }
      
      // 批发商验证：确保购物车只有一个批发商的商品
      const productWholesalerId = product.wholesalerId || product.stallId;
      if (this.cart.length > 0) {
        const existingWholesalerId = this.cart[0].wholesalerId;
        if (existingWholesalerId && productWholesalerId && existingWholesalerId !== productWholesalerId) {
          // 购物车已有其他批发商的商品
          const message = this.$t('stallProduct.otherStallItems');
          
          const confirmed = await this.showConfirm(message);
          if (confirmed) {
            // 用户确认清空购物车
            // ✅ 修复：优先使用批发价
            const price = product.wholesalePrice || product.minWholesalePrice || product.marketPrice || 0;
            
            this.cart = [{
              id: product.id,
              name: product.name,
              price: price,
              quantity: quantity,
              wholesalerId: productWholesalerId,
              wholesalerName: product.wholesalerName || product.stallName,
              taxRate: product.taxRate || 0
            }];
            this.saveCartToLocalStorage();
            this.$toast?.success(this.$t('stallProduct.clearedAndAdded'));
          }
          return;
        }
      }
      
      // 检查是否已存在
      const existing = this.cart.find(item => item.id === product.id);
      if (existing) {
        existing.quantity += quantity;
      } else {
        // ✅ 修复：优先使用批发价，如果没有则使用市场价
        const price = product.wholesalePrice || product.minWholesalePrice || product.marketPrice || 0;
        
        this.cart.push({
          id: product.id,
          name: product.name,
          price: price,
          quantity: quantity,
          wholesalerId: productWholesalerId,
          wholesalerName: product.wholesalerName || product.stallName,
          taxRate: product.taxRate || 0
        });
      }
      // 重置数量
      this.cartQuantities[product.id] = 1;
      // 保存到 localStorage
      this.saveCartToLocalStorage();
      this.$toast?.success(this.$t('stallProduct.addedToCart'));
    },
    loadCartFromLocalStorage() {
      try {
        const cartStr = localStorage.getItem('stall_cart');
        return cartStr ? JSON.parse(cartStr) : [];
      } catch (e) {
        console.error('加载购物车失败:', e);
        return [];
      }
    },
    saveCartToLocalStorage() {
      try {
        localStorage.setItem('stall_cart', JSON.stringify(this.cart));
      } catch (e) {
        console.error('保存购物车失败:', e);
      }
    },
    increaseQty(item) {
      item.quantity += 1;
      // 保存到 localStorage
      this.saveCartToLocalStorage();
    },
    decreaseQty(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.cart = this.cart.filter(i => i.id !== item.id);
        // 同时从选中列表移除
        this.selectedIds = this.selectedIds.filter(id => id !== item.id);
      }
      // 保存到 localStorage
      this.saveCartToLocalStorage();
    },
    // 全选/取消全选
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedIds = this.cartItems.map(item => item.id);
      } else {
        this.selectedIds = [];
      }
    },
    // 删除选中的商品
    async deleteSelected() {
      if (this.selectedIds.length === 0) return;
      
      const message = this.$t('stallProduct.deleteConfirm') + ` ${this.selectedIds.length} ` + this.$t('stallProduct.items');
      
      const confirmed = await this.showConfirm(message, '🗑️');
      if (confirmed) {
        this.cart = this.cart.filter(item => !this.selectedIds.includes(item.id));
        this.selectedIds = [];
        this.selectAll = false;
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('stallProduct.deletedSelected'));
      }
    },
    // 删除单个商品
    async deleteItem(item) {
      const message = this.$t('stallProduct.deleteItemConfirm') + ` "${item.name}"?`;

      const confirmed = await this.showConfirm(message, '🗑️');
      if (confirmed) {
        this.cart = this.cart.filter(i => i.id !== item.id);
        this.selectedIds = this.selectedIds.filter(id => id !== item.id);
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('stallProduct.itemDeleted'));
      }
    },
    async clearCart() {
      if (this.cartItems.length === 0) return;

      const message = this.$t('stallProduct.clearCartConfirm');
      
      const confirmed = await this.showConfirm(message, '🗑️');
      if (confirmed) {
        this.cart = [];
        this.selectedIds = [];
        this.selectAll = false;
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('stallProduct.cartCleared'));
      }
    },
    checkout() {
      // 检查是否有选中的商品
      if (this.selectedIds.length === 0) {
        this.$toast?.warning(this.$t('stallProduct.pleaseSelectItems'));
        return;
      }
      // 跳转到结算页面，传递选中的商品
      this.$router.push({
        path: '/customer/checkout',
        query: {
          items: JSON.stringify(this.selectedItems)
        }
      });
    },
    navigateTo(route) {
      if (this.$route.path !== route) {
        this.$router.push(route).catch(() => {});
      }
    },
    /**
     * 格式化价格：保留有效数字，去掉末尾多余的0，但至少保留2位小数
     * 0.0000 → 0.00, 0.2000 → 0.20, 0.2001 → 0.2001, 1.5000 → 1.50
     */
    formatPrice(value) {
      let str = Number(value).toFixed(4);
      // 去掉末尾多余的0，但至少保留2位小数
      str = str.replace(/(\.\d{2}?)0+$/, '$1');
      // 确保至少2位小数
      if (str.indexOf('.') === -1) str += '.00';
      else if (str.split('.')[1].length < 2) str += '0';
      return str;
    },
    /**
     * 获取商品的图片列表
     */
    getProductImages(product) {
      const images = [];
      if (product.mainImage && product.mainImage.startsWith('http')) {
        images.push(product.mainImage);
      }
      if (product.images) {
        let imgList = product.images;
        if (typeof imgList === 'string') {
          try { imgList = JSON.parse(imgList); } catch(e) { imgList = []; }
        }
        if (Array.isArray(imgList)) {
          imgList.forEach(img => {
            const url = typeof img === 'string' ? img : img.url;
            if (url && url.startsWith('http') && !images.includes(url)) {
              images.push(url);
            }
          });
        }
      }
      return images;
    },
    /**
     * 打开图片预览
     */
    openImagePreview(product) {
      const images = this.getProductImages(product);
      if (images.length > 0) {
        this.$refs.imagePreview.open(images, 0);
      }
    },
    async loadActiveCampaign() {
      try {
        const response = await api.get('/share/active-campaign');
        if (response && response.rewardProductName) {
          this.campaignPrizeName = response.rewardProductName;
        }
      } catch (error) {
        console.error('加载活动信息失败:', error);
      }
    },
    /**
     * 加载批发商 WhatsApp 号码
     */
    async loadWholesalerWhatsapp() {
      try {
        // 优先从 sessionStorage 获取批发商ID
        let wholesalerId = sessionStorage.getItem('url_wholesaler_id');
        
        // 如果没有，从商品列表中获取第一个商品的 wholesalerId
        if (!wholesalerId && this.products.length > 0) {
          wholesalerId = this.products[0].wholesalerId || this.products[0].stallId;
        }
        
        if (!wholesalerId) {
          console.log('No wholesalerId found');
          return;
        }
        
        const response = await api.get('/shop/info', { params: { wholesalerId } });
        if (response) {
          this.wholesalerWhatsapp = response.whatsappNumber || '';
          console.log('Loaded whatsapp:', this.wholesalerWhatsapp);
        }
      } catch (e) {
        console.error('Failed to load wholesaler whatsapp:', e);
      }
    },
    /**
     * WhatsApp 联系商家
     */
    contactWhatsapp(product) {
      if (!this.wholesalerWhatsapp) {
        this.$toast?.warning(this.$t('order.contactWhatsappNoConfig'));
        return;
      }
      const productName = product.name || '';
      const message = `你好，我想咨询一下商品：${productName}`;
      const url = `https://wa.me/${this.wholesalerWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  }
};
</script>

<style scoped>
.stall-products-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 56px;
}

/* ==================== 主体布局 ==================== */
.main-content {
  padding: 12px;
  min-height: calc(100vh - 60px);
}

/* 搜索包装器 */
.search-wrapper {
  flex: 1;
  position: relative;
  min-width: 0;
}

/* 搜索下拉建议 */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  z-index: 200;
  margin-top: 4px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f5f5f5;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:active {
  background: #f0f4ff;
}

.suggestion-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.suggestion-text {
  flex: 1;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-type {
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
}

/* 分类标签栏 */
.category-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding: 4px 0;
}

.category-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  border: 1px solid #eee;
  transition: all 0.2s;
  white-space: nowrap;
}

.category-label.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
}

.category-label:active {
  transform: scale(0.95);
}

.category-emoji {
  font-size: 14px;
}

.category-text {
  font-size: 13px;
}

/* 右侧商品列表 */
.product-list-wrapper {
  flex: 1;
  min-width: 0;
}

/* 头部：搜索 + 购物车 */
.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  transition: border-color 0.2s;
}

.search-box:hover {
  border-color: #667eea;
}

.search-icon {
  margin-right: 8px;
  font-size: 14px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
  color: #333;
}

.search-input::placeholder {
  color: #999;
}

.search-clear {
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
}

.search-clear:hover {
  color: #666;
}

.cart-icon {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 16px;
  cursor: pointer;
  position: relative;
}

.cart-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #f5576c;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
}

.scan-btn {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.scan-btn:hover {
  background: #f0f0f0;
}

.scan-frame-icon {
  width: 22px;
  height: 22px;
  stroke: #667eea;
}

.scan-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ff6b6b;
  color: white;
  border-radius: 6px;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: bold;
  line-height: 1.3;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

/* 商品列表 */
.product-list {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

/* 商品卡片 */
.product-item {
  background: white;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.product-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.product-item:active {
  transform: scale(0.99);
}

/* 商品名称标题 - 整行 */
.product-name-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  padding: 0 0 8px 0;
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.4;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
}

/* 商品主体 - 左图右信息 */
.product-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* 商品图片 */
.product-body .product-image {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

.product-body .product-img {
  width: 60px;
  height: 60px;
  max-width: 100%;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.product-icon {
  font-size: 28px;
}

/* 多图指示器 */
.image-count-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}

/* 商品信息 */
.product-info {
  flex: 1;
  min-width: 0;
}

/* 划掉的市场价 - 弱化显示 */
.market-price-del {
  font-size: 12px;
  color: #ccc;
  text-decoration: line-through;
  margin-right: 6px;
  font-weight: 400;
}

/* 突出的批发价 */
.wholesale-price {
  font-size: 22px;
  font-weight: 800;
  color: #ff4d4f;
}

/* 批发标识 */
.wholesale-badge {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 6px;
}



.product-meta {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.wholesaler-name {
  color: #667eea;
  font-weight: 500;
}

/* 价格 */
.price-column {
  margin-bottom: 8px;
}

.price-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-label {
  font-size: 12px;
  color: #999;
}

.price-value {
  font-size: 18px;
  font-weight: 700;
  color: #ff4d4f;
}

.price-value.market-price-del {
  font-size: 12px;
  font-weight: 400;
  color: #999 !important;
  text-decoration: line-through;
}

.price-value.wholesale {
  color: #52c41a;
}

/* 库存行 - 每行独立显示 */
.stock-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}

/* 数量显示区域 */
.quantity-display {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
}

.quantity-display:hover {
  background: #f0f0f0;
  border-color: #667eea;
}

.edit-arrow {
  font-size: 10px;
  color: #999;
}

/* 数量选择弹窗 */
.quantity-picker-modal {
  background: white;
  border-radius: 12px;
  width: 280px;
  max-width: 90%;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.quantity-picker-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.quantity-picker-modal .modal-header h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.quantity-picker-modal .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.quantity-picker-modal .modal-body {
  margin-bottom: 16px;
}

.quantity-picker {
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.picker-highlight {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 32px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0.15;
  border-radius: 4px;
  pointer-events: none;
  z-index: 2;
}

.picker-scroll {
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.picker-scroll::-webkit-scrollbar {
  display: none;
}

.picker-spacer {
  height: 64px;
  flex-shrink: 0;
}

.picker-item {
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 16px;
  color: #666;
  scroll-snap-align: center;
  user-select: none;
}

.quantity-input-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}

.quantity-input-section label {
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.quantity-input {
  width: 80px;
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.quantity-input:focus {
  outline: none;
  border-color: #667eea;
}

.quantity-picker-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
}

.modal-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-btn:hover {
  opacity: 0.9;
}

.stock-row:last-child {
  margin-bottom: 0;
}

.stock-label {
  color: #666;
  font-size: 13px;
  flex-shrink: 0;
}

.stock-value {
  color: #333;
  font-weight: 600;
}

.stock-value.in-stock {
  color: #10b981;
}

.stock-value.out-of-stock {
  color: #ef4444;
}

.quantity-input {
  width: 70px;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  outline: none;
}

.quantity-input:focus {
  border-color: #667eea;
}

.stock-unit {
  font-size: 13px;
  color: #999;
}

/* 禁用状态 */
.add-to-cart-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

.add-to-cart-btn:disabled:hover {
  opacity: 0.6;
}

.quantity-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.stock-row:has(.quantity-input:disabled) .stock-label {
  color: #ccc;
}

.add-to-cart-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.add-to-cart-btn:hover {
  opacity: 0.9;
}

/* 行动行（购物车+分享） */
.action-row {
  display: flex;
  gap: 8px;
}

.action-row .add-to-cart-btn {
  flex: 0 0 50%;
}

.whatsapp-btn {
  flex: 0 0 auto;
  min-width: 80px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: #25d366;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  gap: 2px;
}

.whatsapp-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.whatsapp-btn:hover:not(:disabled) {
  background: #128c7e;
}

.share-btn {
  width: 80px;
  height: auto;
  border: none;
  border-radius: 6px;
  background: #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
}

.share-icon {
  width: 14px;
  height: 14px;
  margin-bottom: 2px;
  stroke: #1890ff;
}

.share-btn-text {
  font-size: 10px;
  margin-left: 0;
}

.share-btn:hover {
  background: #e0e0e0;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

/* ==================== 弹窗 ==================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999 !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.modal.cart-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  padding: 24px;
  z-index: 10000 !important;
  position: relative;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2);
  opacity: 1 !important;
  visibility: visible !important;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.modal-body {
  margin-bottom: 20px;
}

.empty-cart {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}

.item-checkbox {
  flex-shrink: 0;
  width: 20px;
  display: flex;
  align-items: center;
}

.item-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
  font-weight: 500;
}

.item-wholesaler {
  font-size: 12px;
  color: #667eea;
  margin-bottom: 4px;
}

.item-price {
  font-size: 13px;
  color: #999;
}

.item-tax {
  font-size: 11px;
  color: #16a34a;
  margin-top: 2px;
}

.item-tax-free {
  color: #f59e0b;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.item-actions button {
  width: 28px;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}

.item-actions button:hover {
  background: #f5f5f5;
}

.btn-delete-item {
  background: #fff5f5 !important;
  border-color: #fed7d7 !important;
}

.btn-delete-item:hover {
  background: #fed7d7 !important;
}

/* 批量操作栏 */
.batch-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.select-all-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.batch-buttons {
  display: flex;
  gap: 8px;
}

.btn-delete-batch,
.btn-clear-cart {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-batch {
  background: #fed7d7;
  color: #c53030;
}

.btn-delete-batch:hover:not(:disabled) {
  background: #fc8181;
}

.btn-delete-batch:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-clear-cart {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-clear-cart:hover {
  background: #cbd5e0;
}

/* 金额备注 */
.summary-note {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  font-style: italic;
}

.cart-summary {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #f0f0f0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.summary-row.grand-total {
  font-size: 16px;
  font-weight: bold;
  color: #f5576c;
}

.modal-footer {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-btn.secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.modal-btn.secondary:hover {
  background: #cbd5e0;
}

/* ==================== 响应式 ==================== */
@media (max-width: 768px) {
  .main-layout {
    gap: 12px;
    padding: 12px;
  }
  
  .category-sidebar {
    width: 80px;
    padding: 12px 8px;
  }
  
  .category-icon {
    font-size: 24px;
  }
  
  .category-name {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .main-layout {
    flex-direction: column;
  }
  
  .category-sidebar {
    width: 100%;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 8px;
    padding: 12px;
  }
  
  .category-title {
    display: none;
  }
  
  .category-item {
    min-width: 60px;
    margin-bottom: 0;
    min-height: auto;
  }
}

/* ==================== 加载状态 ==================== */
.loading-more {
  text-align: center;
  padding: 16px;
  color: #999;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.no-more {
  text-align: center;
  padding: 16px;
  color: #ccc;
  font-size: 13px;
}

/* ==================== 底部导航 ==================== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
  position: relative;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.cart-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #f5576c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
}
</style>
